import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Send, Loader2, MessageCircle } from 'lucide-react';
import { proposalsApi } from '../utils/api';

// Relative time formatter — "just now", "5m ago", "2h ago", "3d ago", or date.
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const POLL_INTERVAL_MS = 15_000;

// Forward a ref so the parent ("Ask a Question" button) can focus the textarea.
const ProposalCommentThread = forwardRef(function ProposalCommentThread({ pathId }, ref) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);
  const listEndRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
      textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }));

  const load = useCallback(async () => {
    try {
      const data = await proposalsApi.listComments(pathId);
      setComments(data);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Scroll the newest comment into view whenever the list grows.
  useEffect(() => {
    if (comments.length > 0) {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [comments.length]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const newComment = await proposalsApi.postComment(pathId, trimmed);
      setComments(prev => [...prev, newComment]);
      setDraft('');
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="proposal-comments">
      <div className="proposal-comments-header">
        <h2><MessageCircle size={18} /> Questions &amp; Replies</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="proposal-comments-list">
        {loading ? (
          <div className="proposal-comments-loading">
            <Loader2 className="spinner" size={20} /> Loading…
          </div>
        ) : comments.length === 0 ? (
          <div className="empty-messages">
            No messages yet. Have a question? Type it below and our team will respond.
          </div>
        ) : (
          comments.map(c => (
            <div
              key={c.id}
              className={`message ${c.is_client ? 'sent' : 'received'}`}
            >
              <div className="message-header">
                <span className="sender-name">
                  {c.user_name || (c.is_client ? 'You' : 'Staff')}
                </span>
                <span className="message-time">{relativeTime(c.created_at)}</span>
              </div>
              <div className="message-content">{c.content}</div>
            </div>
          ))
        )}
        <div ref={listEndRef} />
      </div>

      <form className="message-input-container" onSubmit={handleSend}>
        <textarea
          ref={textareaRef}
          className="message-input"
          placeholder="Type your question or comment…"
          rows={2}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            // Cmd/Ctrl + Enter to submit
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSend(e);
          }}
          disabled={sending}
        />
        <button
          type="submit"
          className="send-button"
          disabled={sending || !draft.trim()}
          aria-label="Send"
        >
          {sending ? <Loader2 className="spinner" size={18} /> : <Send size={18} />}
        </button>
      </form>
    </section>
  );
});

export default ProposalCommentThread;
